import React, { useCallback, useMemo, useState } from 'react';
import {
  Image,
  Platform,
  Pressable,
  RefreshControl,
  SectionList,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import ServiceRow from '../components/home/ServiceRow';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import EmptyState from '../components/ui/EmptyState';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
import { Category, Service } from '../data/services';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/**
 * The whole catalogue, laid out flat: category, then every service under it,
 * then the next category - no sheet, no picker, no second tap to see what a
 * trade actually offers.
 *
 * This replaces the old "grid of tiles that opens a sheet" browse path for
 * anyone who lands here directly (the Services tab), rather than by tapping
 * a single category on Home - that shorter, one-category route still goes
 * straight to `SubCategoryScreen`. Here there is no single category in mind,
 * so the page shows all of them and lets search narrow it instead.
 *
 * ---------------------------------------------------------------------------
 * WHY A SECTIONLIST AND NOT A COLUMN OF VIEWS
 * ---------------------------------------------------------------------------
 * It was a column of Views, and the whole catalogue was in it: sixty-odd rows,
 * each with a photograph, all mounted at once - inside a plain `View`, so it
 * could not even scroll. Every keystroke in the search field re-laid the lot.
 *
 * `SectionList` mounts what is on screen and a screenful either side, which is
 * the difference between decoding six images and decoding sixty.
 *
 * The rows themselves are `React.memo`'d, and that only pays off if their
 * props keep their identity - see `renderRow` below.
 */

/*
 * `TRADE_BANNERS` and `AC_BANNER` lived here - one bundled room shot per
 * trade in `assets/small banner/`, plus a dedicated AC one, chosen by mapping
 * the server's slug back to one of the app's own eight categories.
 *
 * Gone with `bannerFor` below, which now reads the category's own photograph.
 * The image files are still in the repo, so if a catalogue ever ships
 * categories with no artwork of their own this is a few lines to put back.
 */

const BANNER_ASPECT = 2172 / 724;

/**
 * Ceiling on the section banner's height, in points.
 *
 * These headers pin while their own services scroll under them, so the height
 * is no longer only a matter of how the photograph looks - it is space taken
 * away from the list for as long as that category is on screen. At the shot's
 * own 3:1 aspect a 430pt phone wants ~143pt of banner, which is a third of the
 * screen standing still while the rows slide past underneath.
 *
 * 104 keeps the trade name and enough of the props to read as the same
 * banner, and leaves the list the rest.
 */
const STUCK_HEIGHT = 104;

/**
 * The category's own photograph, uploaded in the console.
 *
 * ---------------------------------------------------------------------------
 * THE PLATFORM'S PICTURE, NOT THE APP'S GUESS AT ONE
 * ---------------------------------------------------------------------------
 * This used to read `TRADE_BANNERS`, a bundled shot per trade, chosen by
 * mapping the server's slug back to one of the app's own eight categories.
 * That was right while the app had photography and the platform had none.
 *
 * It is wrong now, and wrong in the way that matters: the shot sits directly
 * behind the category's NAME, so a bundled cleaning banner under "House
 * Shifting" reads as a photograph of house shifting. Every live category has
 * its own picture - `ApiCategoryNode.iconUrl`, mapped onto `Category.image` in
 * `services/catalogue.ts` - and that is the one that belongs here.
 *
 * The RATIO does not change. The box is still `bannerWidth` × `bannerHeight`
 * at `BANNER_ASPECT` with `resizeMode="cover"`, so a photograph shot at any
 * other proportion is cropped to the same strip rather than reshaping the
 * header. That is what keeps every section head the same height whatever ops
 * uploads.
 *
 * No bundled fallback. A category with no picture gets the tinted strip and
 * its glyph - see the render - which reads as artwork that has not been added
 * rather than as a photograph of the wrong trade.
 */
const bannerFor = (category: Category) => category.image;

type Props = {
  categories: Category[];
  categoryFor: (service: Service) => Category;
  servicesIn: (key: string) => Service[];
  cart: Record<string, number>;
  onChangeQuantity: (service: Service, delta: number) => void;
  onOpenService: (service: Service) => void;
  /**
   * Whether the address this catalogue is being browsed against is one we can
   * actually reach.
   *
   * Three-valued on purpose, passed straight down from `useCoverage`: `false`
   * draws the coming-soon banner, `true` and `null` (not known yet) both draw
   * nothing. A slow `/cities` must not tell someone in a city we DO cover
   * that we are not there.
   */
  serviceable?: boolean | null;
  /**
   * Re-reads the catalogue behind this page.
   *
   * Optional so a caller that has no catalogue to re-read - a test, a
   * preview - renders a page that simply does not pull, rather than one whose
   * gesture spins and answers with nothing.
   */
  onRefresh?: () => Promise<unknown>;
};

function ServicesScreen({
  categories,
  categoryFor,
  servicesIn,
  cart,
  onChangeQuantity,
  onOpenService,
  serviceable,
  onRefresh,
}: Props) {
  const resp = useResponsive();
  const [query, setQuery] = useState('');

  /*
   * The catalogue is the whole of this page, and ops changes it while people
   * have the app open. Without this the only way to see a new service or a
   * changed price was to kill the app and start again.
   */
  const pull = usePullToRefresh(
    useCallback(
      () => (onRefresh ? onRefresh() : Promise.resolve()),
      [onRefresh],
    ),
  );

  /*
   * Category → its services, in the platform's own category order. Built off
   * `servicesIn` (the server's own grouping) rather than filtering the flat
   * list by `service.category`, for the same reason `SubCategoryScreen`
   * does: a service can sit on a shelf the flat field cannot express.
   */
  const sections = useMemo(
    () =>
      categories
        /* `data` rather than `services`: the name `SectionList` requires. */
        .map(category => ({ category, data: servicesIn(category.key) }))
        .filter(section => section.data.length > 0),
    [categories, servicesIn],
  );

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return sections;
    }
    return sections
      .map(section => ({
        category: section.category,
        data: section.data.filter(service =>
          `${service.name} ${service.blurb}`.toLowerCase().includes(needle),
        ),
      }))
      .filter(section => section.data.length > 0);
  }, [query, sections]);

  /*
   * Stable across renders, so `React.memo` on `ServiceRow` can actually do its
   * job. Passing `service => categoryFor(service.category)` inline - as this
   * screen used to - makes a new function on every keystroke, which changes a
   * prop on all sixty rows and re-renders every one of them, images included.
   */
  const renderRow = useCallback(
    ({ item, index }: { item: Service; index: number }) => (
      <View>
        {index > 0 ? <View style={styles.divider} /> : null}
        <ServiceRow
          service={item}
          category={categoryFor(item)}
          quantity={cart[item.key] ?? 0}
          onChangeQuantity={onChangeQuantity}
          onOpenDetails={onOpenService}
        />
      </View>
    ),
    [cart, categoryFor, onChangeQuantity, onOpenService],
  );

  /*
   * The full device width and a matching height, rather than `100%` plus a
   * negative margin: with `aspectRatio` on a percentage-wide box, a negative
   * `marginHorizontal` does not reliably widen the box back out to the true
   * edge on every engine - the box can size itself against its pre-margin
   * width and leave a visible gap on both sides. Pixel values sidestep that:
   * the banner is exactly `resp.width` wide and pulled left by the page's
   * own gutter, whatever that gutter measures.
   */
  const bannerWidth = resp.width;
  /*
   * The banner's natural height from its own aspect, then capped.
   *
   * At full aspect this is about a third of a phone's screen, and a header
   * that tall pinned to the top covers the rows it is introducing. Capping the
   * height crops the shot rather than squashing it (`resizeMode="cover"`), so
   * the composition survives and the pinned band stays a banner rather than
   * becoming a poster.
   */
  const bannerHeight = Math.min(
    STUCK_HEIGHT,
    Math.round(bannerWidth / BANNER_ASPECT),
  );
  /* From inside the READING column back out to the true screen edge - the
     rows hold to a readable width, the category banners still run full
     bleed. See `reading` in `theme/responsive.ts`. */
  const bleed = -(screenPadding + resp.readingInset);
  const renderHeader = useCallback(
    ({ section }: { section: { category: Category } }) => {
      /*
       * The web build gets the heading and nothing else. The room shots are
       * composed for a phone held upright - wall on the left, props on the
       * right - and a desktop column stretches that composition into a thin
       * cropped strip that reads as decoration, not as a header. No inline
       * box either: the text sits in the list's own gutter like every other
       * heading, and `sectionHead`'s opaque ground is still what keeps the
       * stuck header from dragging rows through itself.
       */
      if (Platform.OS === 'web') {
        return (
          <View style={styles.sectionHead}>
            <Text variant="title" numberOfLines={2}>
              {section.category.label}
            </Text>
          </View>
        );
      }

      const banner = bannerFor(section.category);
      const box = { width: bannerWidth, height: bannerHeight, marginLeft: bleed };

      return (
        <View style={styles.sectionHead}>
          {banner ? (
            <View style={[styles.bannerWrap, box]}>
              <Image source={banner} style={styles.banner} resizeMode="cover" />
              {/* The trade name over the shot's own plain-wall half. The
                  cutout of a professional that used to stand between the two
                  is gone - it overlapped the heading it was meant to sit
                  beside, which is the one thing a section header cannot do. */}
              <Text
                variant="title"
                numberOfLines={2}
                style={styles.bannerLabel}
              >
                {section.category.label}
              </Text>
            </View>
          ) : (
            /* No shot for this trade yet - the tinted strip and its glyph read
               as missing artwork rather than as a mismatch, the same fallback
               a category with no `image` gets everywhere else. */
            <View style={[styles.bannerWrap, styles.bannerFallback, box]}>
              <View style={styles.bannerFallbackMark}>
                <Icon
                  name={section.category.icon}
                  size={22}
                  color={colors.primary}
                  strokeWidth={1.6}
                />
              </View>
              <Text variant="title" numberOfLines={2} style={styles.bannerLabel}>
                {section.category.label}
              </Text>
            </View>
          )}
        </View>
      );
    },
    [bannerWidth, bannerHeight, bleed],
  );

  const renderFooter = useCallback(() => <View style={styles.sectionEnd} />, []);

  const keyOf = useCallback((item: Service) => item.key, []);

  /*
   * Not in this city yet - the message INSTEAD of the catalogue.
   *
   * ---------------------------------------------------------------------------
   * WHY THE WHOLE SCREEN AND NOT A BANNER OVER THE LIST
   * ---------------------------------------------------------------------------
   * A banner above a live catalogue says two things at once: "we cannot come
   * to you" and "here are sixty services, add them to your cart". The second
   * one wins, because it is the whole screen and the banner is four lines, and
   * the result is someone picking services, filling a basket and meeting the
   * wall at checkout - which is the exact failure the coming-soon message was
   * written to move earlier.
   *
   * So there is nothing else on the screen. Not the search field either: a
   * search box over no results is an invitation to discover the emptiness one
   * query at a time.
   *
   * Home is deliberately NOT this. It keeps its catalogue and carries a
   * banner, because someone may be browsing on behalf of a relative in a city
   * we DO serve, and because Home is also the way to the address switcher.
   * This tab has one job - pick a service - and that job is not available.
   *
   * Only for an explicit `false`. `null` (not known yet) and `true` both fall
   * through to the catalogue, so a slow `/cities` never hides the whole
   * screen from a customer we can serve.
   */
  if (serviceable === false) {
    return (
      <View style={styles.root}>
        <View style={styles.blocked}>
          <EmptyState
            icon="pin"
            title="We're coming to your city soon"
            message={
              "We don't serve this area yet. We're expanding fast - we'll let you know the moment we arrive."
            }
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      {/* Universal - the same field whichever category is on screen below
          it, so search is never scoped to only one shelf here. */}
      <View style={[styles.searchBar, { paddingHorizontal: screenPadding + resp.readingInset }]}>
        <View style={styles.searchField}>
          <Icon name="search" size={18} color={colors.inkMuted} strokeWidth={1.8} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search all services"
            placeholderTextColor={colors.inkSubtle}
            style={styles.searchInput}
            accessibilityLabel="Search all services"
            returnKeyType="search"
          />
          {query.length > 0 ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              onPress={() => setQuery('')}
              hitSlop={10}
            >
              <Icon
                name="plus"
                size={16}
                color={colors.inkMuted}
                strokeWidth={2.2}
                style={styles.clearGlyph}
              />
            </Pressable>
          ) : null}
        </View>
      </View>

      <SectionList
        sections={shown}
        {...(onRefresh
          ? {
              refreshControl: (
                <RefreshControl
                  refreshing={pull.refreshing}
                  onRefresh={pull.onRefresh}
                  tintColor={colors.primary}
                  colors={[colors.primary]}
                />
              ),
            }
          : null)}
        keyExtractor={keyOf}
        renderItem={renderRow}
        renderSectionHeader={renderHeader}
        renderSectionFooter={renderFooter}
        /*
         * On: each category's banner pins to the top while its own services
         * scroll under it, and is pushed off by the next category's banner.
         *
         * The banner is tall for something pinned - that was the reason this
         * was off - so `bannerHeight` is capped when it is stuck (see
         * `STUCK_HEIGHT`), which keeps the trade visible without the photo
         * taking a third of the screen away from the rows it introduces.
         */
        stickySectionHeadersEnabled
        /* `reading`, not `page`: service rows are label-price reading
           content, and on the web's wide page column they stretched a title
           to one edge and its Add button to the other. */
        contentContainerStyle={[styles.list, resp.reading]}
        keyboardShouldPersistTaps="handled"
        /*
          Detaches off-screen rows from the native view tree on Android, which
          is what keeps a sixty-row catalogue scrolling smoothly.
        */
        removeClippedSubviews
        /* Two screens' worth kept live either side: enough that a fast flick
           does not reach blank space, few enough that the images off screen
           are not decoded. */
        windowSize={5}
        initialNumToRender={8}
        maxToRenderPerBatch={8}
        ListEmptyComponent={
          <EmptyState
            icon="search"
            title="Nothing matches"
            message={`No service or category matches "${query.trim()}".`}
          />
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  /* Centres the message in the space the catalogue would have filled, rather
     than leaving it stuck under the top edge. */
  blocked: {
    flex: 1,
    justifyContent: 'center',
  },
  searchBar: {
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    backgroundColor: colors.surface,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  /* The same field the search screen draws - pill, white, hairline - so the
     two read as one control rather than as two different search boxes. */
  searchField: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    height: 52,
    paddingHorizontal: spacing.md,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  searchInput: {
    flex: 1,
    ...inputText,
  },
  clearGlyph: {
    transform: [{ rotate: '45deg' }],
  },
  list: {
    paddingBottom: spacing.xxxl,
  },
  sectionHead: {
    paddingTop: spacing.lg,
    paddingBottom: spacing.xs,
    /* No horizontal padding: the banner runs to the true screen edge and
       pulls itself back out of the list's gutter with a negative margin -
       see `bleed` in the component. */
    /* Opaque: a sticky header with a transparent ground drags the rows
       underneath it through itself as they scroll past. */
    backgroundColor: colors.background,
  },
  /*
   * Runs to the true screen edge, not a rounded card sitting in the page's
   * own gutter - width, height and the pull-left margin are all computed
   * per-render from `resp` and passed inline (see `box` in `renderHeader`).
   * No `overflow: hidden`/radius: this is a strip of the page, not a tile.
   */
  bannerWrap: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  banner: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  bannerLabel: {
    flexShrink: 1,
    /*
     * On the page's own gutter.
     *
     * This used to be pushed right by an inline `marginLeft`, computed from
     * where the cutout of the professional ended - the label had to start
     * past their shoulder. With the cutout gone there is nothing to clear, so
     * the heading starts where every other heading on the page starts.
     */
    marginLeft: screenPadding,
    paddingRight: screenPadding,
    /*
     * Held to the plain-wall half of the shot.
     *
     * Every one of these banners is composed the same way: empty wall on the
     * left, and the props - a washing machine, a tap, a plant - massed on the
     * right. Left unbounded a two-word category runs on into that furniture
     * and the words sit on top of the objects.
     *
     * 58% now the figure is gone: the label starts at the gutter rather than
     * a third of the way across, so it can be wider and still stop short of
     * the props. That width is what lets "Carpentry and Installation" wrap to
     * the two lines it is allowed instead of losing its tail to an ellipsis.
     */
    maxWidth: '58%',
  },
  bannerFallback: {
    backgroundColor: colors.surfaceAccent,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  bannerFallbackMark: {
    width: 40,
    height: 40,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
  },
  divider: {
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  /* Heavier than the row-to-row divider above - this closes off a whole
     category, not just one service from the next. */
  sectionEnd: {
    marginTop: spacing.lg,
    borderTopWidth: spacing.sm,
    borderTopColor: colors.surfaceMuted,
  },
});

export default ServicesScreen;
